import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Save, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import ImageUploader from "@/components/ImageUploader";

const empty = {
  slug: "", title: "", meta_description: "", h1: "", intro: "",
  content: "", cover_image: "", canonical: "", is_published: true, keywords: "",
};

export default function SeoPagesAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/seo-pages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setError("");
    try {
      const payload = { ...edit };
      if (edit.id) await api.put(`/seo-pages/${edit.id}`, payload);
      else await api.post("/seo-pages", payload);
      setEdit(null); load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const del = async (id) => { if (window.confirm("Supprimer cette page SEO ?")) { await api.delete(`/seo-pages/${id}`); load(); } };

  return (
    <div data-testid="admin-seo-page">
      <div className="flex justify-between items-end">
        <div>
          <p className="overline">SEO</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Pages SEO</h1>
          <p className="text-[#697585] mt-2 text-sm">Pages thématiques avec URL personnalisée (préservation des URLs WordPress).</p>
        </div>
        <button data-testid="add-seo-btn" onClick={() => setEdit({ ...empty })} className="btn-primary"><Plus size={14}/> Nouvelle page</button>
      </div>

      <div className="mt-10 space-y-3">
        {items.map((p) => (
          <div key={p.id} className="border border-[#D9D3C5] bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl text-[#203240]">{p.title}</p>
                <p className="text-xs text-[#697585] mt-1 truncate">/{p.slug}</p>
                {p.meta_description && <p className="text-sm text-[#697585] mt-2 line-clamp-2">{p.meta_description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-widest text-[#B8956A]">{p.is_published ? "publié" : "brouillon"}</span>
                <Link to={`/${p.slug}`} target="_blank" className="p-2 border border-[#D9D3C5] hover:border-[#B8956A]"><ExternalLink size={14}/></Link>
                <button onClick={() => setEdit(p)} className="p-2 border border-[#D9D3C5] hover:border-[#B8956A]"><Pencil size={14}/></button>
                <button onClick={() => del(p.id)} className="p-2 border border-[#D9D3C5] hover:border-red-400 hover:text-red-400"><Trash2 size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-[#697585]">Aucune page SEO.</p>}
      </div>

      {edit && (
        <Modal title={edit.id ? "Modifier la page SEO" : "Nouvelle page SEO"} onClose={() => setEdit(null)}>
          <div className="space-y-5">
            <input className="field" placeholder="Titre (balise <title>)" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="seo-title" />
            <input className="field" placeholder="Slug (ex: restaurant-groupes-versailles)" value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} data-testid="seo-slug" />
            <input className="field" placeholder="Meta description" value={edit.meta_description} onChange={(e) => setEdit({ ...edit, meta_description: e.target.value })} data-testid="seo-desc" />
            <input className="field" placeholder="Mots-clés (séparés par virgules)" value={edit.keywords} onChange={(e) => setEdit({ ...edit, keywords: e.target.value })} />
            <input className="field" placeholder="H1 (titre principal visible)" value={edit.h1} onChange={(e) => setEdit({ ...edit, h1: e.target.value })} />
            <ImageUploader label="Image de couverture" value={edit.cover_image} onChange={(url) => setEdit({ ...edit, cover_image: url })} />
            <input className="field" placeholder="URL canonique (optionnel)" value={edit.canonical} onChange={(e) => setEdit({ ...edit, canonical: e.target.value })} />
            <textarea className="field" placeholder="Intro (paragraphe d'accroche)" rows={3} value={edit.intro} onChange={(e) => setEdit({ ...edit, intro: e.target.value })} />
            <textarea className="field" placeholder="Contenu — Markdown léger : ## titre, **gras**, - liste" rows={12} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} data-testid="seo-content" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.is_published} onChange={(e) => setEdit({ ...edit, is_published: e.target.checked })} /> Publié</label>
            {error && <p className="text-sm text-red-400">{error}</p>}
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button onClick={() => setEdit(null)} className="btn-ghost">Annuler</button>
            <button onClick={save} data-testid="seo-save" className="btn-primary"><Save size={14}/> Enregistrer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#D9D3C5] w-full max-w-3xl p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-6"><h2 className="font-display text-2xl">{title}</h2><button onClick={onClose}><X size={20}/></button></div>
        {children}
      </div>
    </div>
  );
}
